/**
 * useRemoveItineraryByRef — shared resolver for remove_from_itinerary
 * (host pages + agent actions). ONE implementation so ChatView and
 * HostActionBridge can't drift (the 2026-07-09 bug: the host path had no
 * remove wiring at all).
 *
 * Resolution order — IDEMPOTENT, keyed on stable ids per the product owner:
 *   1. (provider, activity_id) exact — the cross-surface key. Explore sends
 *      provider='explore', activity_id=<place slug> for curated pins and the
 *      provider-canonical pair for bookable tours.
 *   2. activity_id alone (provider omitted by older callers).
 *   3. Normalized title — legacy fallback only.
 * Removing something already absent is a successful no-op.
 */
import { useCallback } from 'react';
import { useItinerary } from '../context/ItineraryContext';

export interface RemoveRef {
  activity_id?: string;
  provider?: string;
  slug?: string; // legacy alias for activity_id from early explore builds
  title?: string;
}

const norm = (t: string) =>
  t
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export function useRemoveItineraryByRef(): (ref: RemoveRef) => boolean {
  const { items, removeItem } = useItinerary();
  return useCallback(
    (ref: RemoveRef) => {
      const aid = ref.activity_id || ref.slug;
      let hit = aid
        ? items.find(
            it =>
              it.activity_id === aid &&
              (!ref.provider || (it.provider ?? '') === ref.provider),
          )
        : undefined;
      if (!hit && aid) hit = items.find(it => it.activity_id === aid);
      if (!hit && ref.title) {
        const wanted = norm(ref.title);
        hit = items.find(it => norm(it.title) === wanted);
      }
      if (hit) removeItem(hit.id);
      return !!hit; // absent = successful no-op (idempotent)
    },
    [items, removeItem],
  );
}
